import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getCountFromServer,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { useDialog } from '../components/Dialog';
import { useToast } from '../components/Toast';
import {
  adminApproveDriverVerification,
  adminReviewDriverDocument,
  adminSetUserSuspended,
  adminDeleteUser,
} from '../lib/api';

interface DriverDocumentEntry {
  url?: string;
  frontUrl?: string;
  backUrl?: string;
  status?: 'pending_review' | 'approved' | 'rejected';
  uploadedAt?: unknown;
  rejectionReason?: string | null;
  expiresAt?: string | null;
}

const DOC_TYPES: { key: string; label: string; expires: boolean }[] = [
  { key: 'driversLicense', label: "Driver's License", expires: true },
  { key: 'vehicleInsurance', label: 'Vehicle Insurance', expires: true },
  { key: 'vehicleRegistration', label: 'Vehicle Registration', expires: true },
  { key: 'vehiclePhoto', label: 'Vehicle Photo', expires: false },
];

interface UserRow {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  isOnline?: boolean;
  isActive?: boolean;
  driverVerified?: boolean;
  verificationStatus?: 'pending' | 'approved' | 'rejected';
  driverDocuments?: Record<string, DriverDocumentEntry>;
  profileData?: {
    vehicleType?: string;
    trailerTypes?: string[];
    [k: string]: unknown;
  };
  roles?: string[];
  [key: string]: unknown;
}

interface Props {
  title: string;
  subtitle?: string;
  userType: 'driver' | 'customer';
}

const PAGE_SIZE = 100;

export default function UsersPage({ title, subtitle, userType }: Props) {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [rows, setRows] = useState<UserRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [docsUser, setDocsUser] = useState<UserRow | null>(null);
  const [expiryDrafts, setExpiryDrafts] = useState<Record<string, string>>({});
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { confirm, prompt, dialog } = useDialog();
  const toast = useToast();

  // Keep the filter in sync when the URL ?q= changes (e.g. from global search).
  const qParam = searchParams.get('q') ?? '';
  useEffect(() => {
    setSearch(qParam);
  }, [qParam]);

  useEffect(() => {
    // Users are stored with `roles: ['driver'|'customer'|'admin']`. We avoid
    // an orderBy here because some legacy docs lack `createdAt` and would be
    // silently filtered out.
    const q = query(
      collection(db, 'users'),
      where('roles', 'array-contains', userType),
      limit(pageLimit)
    );
    const unsub = onSnapshot(q, (snap) => {
      setRows(
        snap.docs.map((d) => ({ ...(d.data() as UserRow), id: d.id }))
      );
    });
    return unsub;
  }, [userType, pageLimit]);

  // Total for the role (approximate; refreshed when the role changes).
  useEffect(() => {
    let active = true;
    getCountFromServer(
      query(collection(db, 'users'), where('roles', 'array-contains', userType))
    )
      .then((snap) => {
        if (active) setTotalCount(snap.data().count);
      })
      .catch(() => {
        if (active) setTotalCount(null);
      });
    return () => {
      active = false;
    };
  }, [userType]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      r.email?.toLowerCase().includes(s) ||
      r.firstName?.toLowerCase().includes(s) ||
      r.lastName?.toLowerCase().includes(s) ||
      r.phone?.toLowerCase().includes(s) ||
      r.id.toLowerCase().includes(s)
    );
  });

  const onSuspendToggle = async (u: UserRow) => {
    const isCurrentlySuspended = u.isActive === false;
    const next = !isCurrentlySuspended;
    let reason: string | null = 'Reinstated';
    if (next) {
      reason = await prompt({
        title: 'Suspend user',
        message: `Suspending ${u.email ?? u.id}. Provide a reason for the audit log.`,
        placeholder: 'Reason',
        required: true,
        destructive: true,
        confirmLabel: 'Suspend',
      });
      if (reason === null) return;
    } else {
      const ok = await confirm({
        title: 'Reinstate user',
        message: `Reinstate ${u.email ?? u.id}?`,
        confirmLabel: 'Reinstate',
      });
      if (!ok) return;
    }
    setBusy(u.id);
    try {
      await adminSetUserSuspended({
        uid: u.id,
        suspended: next,
        reason: reason || undefined,
      });
      toast.success(next ? 'User suspended.' : 'User reinstated.');
    } catch (e) {
      toast.error(`Failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (u: UserRow) => {
    const label = u.email ?? u.id;
    const ok = await confirm({
      title: 'Delete account',
      message: `Permanently delete ${label}? This removes their login and profile. Orders and jobs are kept for records. This cannot be undone.`,
      destructive: true,
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
    });
    if (!ok) return;
    setBusy(u.id);
    try {
      await adminDeleteUser({ uid: u.id });
      toast.success('Account deleted.');
    } catch (e) {
      toast.error(`Delete failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(null);
    }
  };

  const onApprove = async (u: UserRow, approved: boolean) => {
    const notes = await prompt({
      title: approved ? 'Approve driver' : 'Reject driver',
      message: `${approved ? 'Approve' : 'Reject'} ${u.email ?? u.id}? Optional notes for the audit log.`,
      placeholder: 'Notes (optional)',
      multiline: true,
      destructive: !approved,
      confirmLabel: approved ? 'Approve' : 'Reject',
    });
    if (notes === null) return;
    setBusy(u.id);
    try {
      // Call the privileged callable (writes audit log + driver_profiles
      // mirror + ideally the user doc).
      await adminApproveDriverVerification({
        uid: u.id,
        approved,
        notes: notes || undefined,
      });
      // Belt-and-suspenders: also write the verification flags directly
      // to the user doc from the admin client. This guarantees the
      // driver app sees the change even if the deployed Cloud Function
      // is an older revision that doesn't mirror to users/{uid}.
      // Admin writes are permitted by firestore.rules `allow update: if isAdmin()`.
      await setDoc(
        doc(db, 'users', u.id),
        {
          driverVerified: approved,
          verificationStatus: approved ? 'approved' : 'rejected',
          verificationNotes: notes || null,
          verifiedAt: serverTimestamp(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      toast.success(approved ? 'Driver approved.' : 'Driver rejected.');
    } catch (e) {
      toast.error(`Failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(null);
    }
  };

  const onReviewDocument = async (
    u: UserRow,
    docKey: string,
    approved: boolean,
    expiresAt?: string
  ) => {
    let reason: string | null = null;
    if (!approved) {
      reason = await prompt({
        title: 'Reject document',
        message: 'Provide a reason shown to the driver.',
        placeholder: 'e.g. Photo is blurry, document expired…',
        required: true,
        destructive: true,
        confirmLabel: 'Reject',
      });
      if (reason === null) return;
    }
    setBusy(u.id);
    try {
      // Route through the audited callable (writes admin_audit_log + the
      // users/{uid}.driverDocuments.{docKey} decision in one place).
      await adminReviewDriverDocument({
        uid: u.id,
        docType: docKey,
        approved,
        reason: approved ? undefined : reason || undefined,
        expiresAt: approved ? expiresAt : undefined,
      });
      // Keep the modal's user object in sync
      setDocsUser((prev) =>
        prev && prev.id === u.id
          ? {
              ...prev,
              driverDocuments: {
                ...prev.driverDocuments,
                [docKey]: {
                  ...prev.driverDocuments?.[docKey],
                  status: approved ? 'approved' : 'rejected',
                  rejectionReason: approved ? null : reason,
                  expiresAt: approved ? expiresAt ?? null : null,
                },
              },
            }
          : prev
      );
      toast.success(approved ? 'Document approved.' : 'Document rejected.');
    } catch (e) {
      toast.error(`Failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />
      {dialog}
      <Card>
        <input
          type="search"
          placeholder="Search by name, email, phone, UID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none sm:max-w-md"
        />
        <div className="mb-4 text-xs text-slate-400">
          {search.trim()
            ? `${filtered.length} match${
                filtered.length === 1 ? '' : 'es'
              } in ${rows.length} loaded`
            : totalCount != null
              ? `Showing ${rows.length.toLocaleString()} of ${Math.max(
                  totalCount,
                  rows.length
                ).toLocaleString()}`
              : `${rows.length} loaded`}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Phone</th>
                {userType === 'driver' && (
                  <>
                    <th className="px-3 py-2 text-left">Vehicle</th>
                    <th className="px-3 py-2 text-left">Online</th>
                    <th className="px-3 py-2 text-left">Verified</th>
                  </>
                )}
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-3 py-2">{u.email || '—'}</td>
                  <td className="px-3 py-2">{u.phone || '—'}</td>
                  {userType === 'driver' && (
                    <>
                      <td className="px-3 py-2">
                        {u.profileData?.vehicleType || '—'}
                        {Array.isArray(u.profileData?.trailerTypes) &&
                          u.profileData!.trailerTypes!.length > 0 && (
                            <div className="text-xs text-slate-500">
                              + {u.profileData!.trailerTypes!.join(', ')}
                            </div>
                          )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            u.isOnline
                              ? 'bg-green-100 text-green-800'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {u.isOnline ? 'online' : 'offline'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const status =
                            u.verificationStatus ??
                            (u.driverVerified ? 'approved' : 'pending');
                          const cls =
                            status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800';
                          return (
                            <span
                              className={`rounded px-2 py-0.5 text-xs ${cls}`}
                            >
                              {status}
                            </span>
                          );
                        })()}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2">
                    {u.isActive === false ? (
                      <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                        suspended
                      </span>
                    ) : (
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {userType === 'driver' && (
                        <button
                          onClick={() => {
                            setDocsUser(u);
                            setExpiryDrafts({});
                          }}
                          disabled={busy === u.id}
                          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-60"
                        >
                          Docs
                          {(() => {
                            const pending = DOC_TYPES.filter(
                              (t) =>
                                u.driverDocuments?.[t.key]?.status ===
                                'pending_review'
                            ).length;
                            return pending > 0 ? (
                              <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-amber-800">
                                {pending}
                              </span>
                            ) : null;
                          })()}
                        </button>
                      )}
                      {userType === 'driver' && !u.driverVerified && (
                        <>
                          <button
                            onClick={() => onApprove(u, true)}
                            disabled={busy === u.id}
                            className="rounded border border-green-300 px-2 py-1 text-xs text-green-800 hover:bg-green-50 disabled:opacity-60"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => onApprove(u, false)}
                            disabled={busy === u.id}
                            className="rounded border border-red-300 px-2 py-1 text-xs text-red-800 hover:bg-red-50 disabled:opacity-60"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => onSuspendToggle(u)}
                        disabled={busy === u.id}
                        className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-60"
                      >
                        {u.isActive === false ? 'Reinstate' : 'Suspend'}
                      </button>
                      {!u.roles?.includes('admin') && (
                        <button
                          onClick={() => onDelete(u)}
                          disabled={busy === u.id}
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-60"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={userType === 'driver' ? 8 : 5}
                    className="px-3 py-8 text-center text-sm text-slate-500"
                  >
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(totalCount != null
          ? rows.length < totalCount
          : rows.length >= pageLimit) && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setPageLimit((n) => n + PAGE_SIZE)}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Load more
            </button>
          </div>
        )}
      </Card>
      {docsUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDocsUser(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Documents —{' '}
                {[docsUser.firstName, docsUser.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                  docsUser.email ||
                  docsUser.id}
              </h2>
              <button
                onClick={() => setDocsUser(null)}
                className="rounded px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              {DOC_TYPES.map(({ key, label, expires }) => {
                const entry = docsUser.driverDocuments?.[key];
                const isLicense = key === 'driversLicense';
                const frontUrl = isLicense
                  ? (entry?.frontUrl ?? entry?.url)
                  : entry?.url;
                const backUrl = isLicense ? entry?.backUrl : undefined;
                const hasImage = isLicense
                  ? Boolean(frontUrl || backUrl)
                  : Boolean(entry?.url);
                const status = hasImage
                  ? (entry?.status ?? 'pending_review')
                  : 'missing';
                const cls =
                  status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : status === 'pending_review'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-600';
                return (
                  <div
                    key={key}
                    className="rounded border border-slate-200 p-3"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <div className="font-medium">{label}</div>
                      <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>
                        {status.replace('_', ' ')}
                      </span>
                    </div>
                    {hasImage ? (
                      <>
                        {isLicense ? (
                          <div className="grid grid-cols-2 gap-2">
                            {(
                              [
                                { side: 'Front', src: frontUrl },
                                { side: 'Back', src: backUrl },
                              ] as const
                            ).map(({ side, src }) => (
                              <div key={side}>
                                <div className="mb-1 text-xs font-medium text-slate-500">
                                  {side}
                                </div>
                                {src ? (
                                  <a
                                    href={src}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block"
                                  >
                                    <img
                                      src={src}
                                      alt={`${label} ${side}`}
                                      className="max-h-56 w-full rounded border border-slate-100 object-contain"
                                    />
                                  </a>
                                ) : (
                                  <div className="flex h-24 items-center justify-center rounded border border-dashed border-slate-200 text-xs text-slate-400">
                                    Not uploaded
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <a
                            href={entry?.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block"
                          >
                            <img
                              src={entry?.url}
                              alt={label}
                              className="max-h-56 rounded border border-slate-100 object-contain"
                            />
                          </a>
                        )}
                        {status === 'rejected' && entry?.rejectionReason && (
                          <div className="mt-2 text-xs text-red-700">
                            Reason: {entry.rejectionReason}
                          </div>
                        )}
                        {expires && (
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                            <label className="text-slate-600">
                              Expiration date:
                            </label>
                            <input
                              type="date"
                              value={
                                expiryDrafts[key] ??
                                entry?.expiresAt?.slice(0, 10) ??
                                ''
                              }
                              onChange={(e) =>
                                setExpiryDrafts((d) => ({
                                  ...d,
                                  [key]: e.target.value,
                                }))
                              }
                              className="rounded border border-slate-300 px-2 py-1"
                            />
                            {status === 'approved' &&
                              entry?.expiresAt &&
                              (() => {
                                const exp = new Date(entry.expiresAt);
                                const expired = exp.getTime() < Date.now();
                                return (
                                  <span
                                    className={
                                      expired
                                        ? 'font-medium text-red-700'
                                        : 'text-slate-500'
                                    }
                                  >
                                    {expired ? 'Expired' : 'Valid until'}{' '}
                                    {exp.toLocaleDateString()}
                                  </span>
                                );
                              })()}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() =>
                              onReviewDocument(
                                docsUser,
                                key,
                                true,
                                expires
                                  ? expiryDrafts[key] ||
                                    entry?.expiresAt?.slice(0, 10) ||
                                    undefined
                                  : undefined
                              )
                            }
                            disabled={
                              busy === docsUser.id || status === 'approved'
                            }
                            className="rounded border border-green-300 px-2 py-1 text-xs text-green-800 hover:bg-green-50 disabled:opacity-60"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() =>
                              onReviewDocument(docsUser, key, false)
                            }
                            disabled={
                              busy === docsUser.id || status === 'rejected'
                            }
                            className="rounded border border-red-300 px-2 py-1 text-xs text-red-800 hover:bg-red-50 disabled:opacity-60"
                          >
                            Reject
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-slate-500">
                        Not uploaded yet.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
